import classNames from 'classnames'
import { createElement } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { Icon } from '../../product-2d-builder/components/ui/Icon/Icon'
import { CarabinerGalleryMedia, CarabinerProduct } from '../types'
import {
  expandMetalLabel,
  formatPrice,
  formatReadyWord,
  getActiveLength,
  getBiLineWithLength,
  getInitialVariantId,
  getGalleryMediaPosterUrl,
  getMatchingVariantForMetal,
  getProductMetal,
  getProductionDate,
  getVariantById,
  getVariantHeroImage,
  parseProductDetailsItems,
  setVariantInUrl
} from '../utils'
import { Text } from '../../product-2d-builder/components/ui/Text/Text'

type ProductDetailsItem = {
  id: string
  title: string
  content: string
}

type Props = {
  product: CarabinerProduct
  linkedProducts: CarabinerProduct[]
}

declare global {
  interface Window {
    handleGalleryProdgessBar?: (sliderElement?: HTMLDivElement | null) => (() => void) | void
  }
}

declare var GM_STATE: type_GM_STATE

const getGalleryMediaKey = (media: CarabinerGalleryMedia, index: number) => {
  if (media.type === 'image') {
    return media.src || `${index}`
  }

  return media.poster || media.sources[0]?.url || `${index}`
}

const getProductDetailsItems = (
  sourceItems: ProductDetailsItem[],
  currentProduct: CarabinerProduct,
  activeVariantDescription: string,
  activeVariantDetails: string
) => {
  return sourceItems.map((item) => {
    const normalizedTitle = item.title.trim().toLowerCase()

    if (normalizedTitle === 'description') {
      return {
        ...item,
        content: activeVariantDescription || currentProduct.descriptionHtml
      }
    }

    if (normalizedTitle === 'details') {
      return {
        ...item,
        content: activeVariantDetails || item.content
      }
    }

    return item
  }).filter((item) => item.content.trim().length > 0 || item.title.trim().toLowerCase() !== 'details')
}

export const App = ({ product, linkedProducts }: Props) => {
  const [currentProduct, setCurrentProduct] = useState(product)
  const [activeVariantId, setActiveVariantId] = useState(() => getInitialVariantId(product))
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [productDetailsItems, setProductDetailsItems] = useState<ProductDetailsItem[]>([])
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const hasInitializedActiveItem = useRef(false)
  const hasInitializedGalleryPosition = useRef(false)
  const gallerySliderRef = useRef<HTMLDivElement | null>(null)
  const galleryListRef = useRef<HTMLDivElement | null>(null)
  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const activeVariant = useMemo(() => {
    return getVariantById(currentProduct, activeVariantId) || currentProduct.variants[0]
  }, [activeVariantId, currentProduct])

  const activeLength = activeVariant ? getActiveLength(currentProduct, activeVariant) : ''
  const currentProductBiLine = activeVariant?.metafields.pdpBiLine || currentProduct.metafields.pdpBiLine
  const currentProductBiLineWithLength = getBiLineWithLength(currentProductBiLine, activeLength)
  const productionDate = getProductionDate(currentProduct)
  const estimatedDispatchLabel = productionDate ? `Estimated dispatch on ${productionDate}` : formatReadyWord()
  const dispatchLabel = activeVariant?.inventoryQuantity ? formatReadyWord() : estimatedDispatchLabel
  const dispatchDisclaimer = activeVariant?.metafields.dispatchDisclaimer
  const heroImageUrl = activeVariant ? getVariantHeroImage(currentProduct, activeVariant) : currentProduct.featuredImage
  const galleryMedia = useMemo<CarabinerGalleryMedia[]>(() => {
    if (activeVariant?.gallery.length) {
      return activeVariant.gallery
    }

    return heroImageUrl ? [{ type: 'image', src: heroImageUrl }] : []
  }, [activeVariant, heroImageUrl])
  const isSoldOut = !activeVariant?.available
  const showNotifyMe = isSoldOut && currentProduct.globalSettings.klaviyoBisEnabled
  const notifyMeText = currentProduct.globalSettings.notifyMeText || 'Notify Me When Available'
  const isAvailable = currentProduct.variants.some((variant) => variant.available)
  const showFindInStore = Boolean(
    currentProduct.globalSettings.enableBauCtas &&
      isAvailable &&
      currentProduct.globalSettings.findStoreUrl &&
      currentProduct.globalSettings.firstAvailableVariantId &&
      currentProduct.globalSettings.shopPermanentDomain
  )
  const materialOptions = [product, ...linkedProducts].map((item) => ({
    id: item.id,
    product: item,
    value: getProductMetal(item)
  }))

  const renderWishlistButton = () => {
    if (!activeVariant || !GM_STATE.wishlist.enable) {
      return null
    }

    return createElement('wishlist-button', {
      className: 'wishlist-button js-wishlist-button js-scroll-to-position block',
      product: JSON.stringify(currentProduct),
      'product-bi-line': currentProductBiLineWithLength,
      'variant-selected': JSON.stringify(activeVariant),
      'variant-selected-id': String(activeVariant.id),
      'template-suffix': currentProduct.templateSuffix,
      disabled: 'false',
      'error-message': '',
      'is-show-only-icon': 'true',
      'product-personalised-price': '0',
      'use-product-slideout': 'true'
    })
  }

  useEffect(() => {
    if (!activeVariantId) {
      return
    }

    setVariantInUrl(currentProduct.url, activeVariantId)
  }, [activeVariantId, currentProduct.url])

  useEffect(() => {
    const galleryList = galleryListRef.current

    if (!galleryList) {
      return
    }

    if (!hasInitializedGalleryPosition.current) {
      hasInitializedGalleryPosition.current = true
      return
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
      galleryList.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
    }
  }, [activeVariantId])

  useEffect(() => {
    const galleryList = galleryListRef.current

    if (!galleryList) {
      return
    }

    const videoElements = Array.from(galleryList.querySelectorAll<HTMLVideoElement>('[data-carabiner-gallery-video="true"]'))

    if (videoElements.length === 0) {
      return
    }

    const pauseVideo = (videoElement: HTMLVideoElement) => {
      videoElement.pause()
      videoElement.currentTime = 0
    }

    const playVideo = (videoElement: HTMLVideoElement) => {
      const playPromise = videoElement.play()

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoElement = entry.target as HTMLVideoElement

          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            playVideo(videoElement)
            return
          }

          pauseVideo(videoElement)
        })
      },
      { threshold: [0.6] }
    )

    videoElements.forEach((videoElement) => {
      observer.observe(videoElement)
    })

    return () => {
      observer.disconnect()
      videoElements.forEach((videoElement) => {
        pauseVideo(videoElement)
      })
    }
  }, [activeVariantId, galleryMedia])

  useEffect(() => {
    const gallerySlider = gallerySliderRef.current

    if (!gallerySlider || typeof window.handleGalleryProdgessBar !== 'function') {
      return
    }

    return window.handleGalleryProdgessBar(gallerySlider)
  }, [activeVariantId, galleryMedia])

  useEffect(() => {
    const source = document.getElementById('carabiner-product-tabs-source')

    if (!source) {
      setProductDetailsItems([])
      return
    }

    const sourceItems = parseProductDetailsItems(source)
    const nextItems = getProductDetailsItems(
      sourceItems,
      currentProduct,
      activeVariant?.metafields.description || '',
      activeVariant?.metafields.details || ''
    )

    setProductDetailsItems(nextItems)
  }, [activeVariant, currentProduct])

  useEffect(() => {
    if (productDetailsItems.length === 0) {
      setActiveItemId(null)
      return
    }

    const initialItemId = productDetailsItems.find((item) => item.title.trim().toLowerCase() === 'description')?.id || productDetailsItems[0]?.id || null

    if (!hasInitializedActiveItem.current) {
      hasInitializedActiveItem.current = true
      setActiveItemId(initialItemId)
      return
    }

    setActiveItemId((currentItemId) => {
      if (!currentItemId) {
        return initialItemId
      }

      return productDetailsItems.some((item) => item.id === currentItemId) ? currentItemId : initialItemId
    })
  }, [productDetailsItems])

  useEffect(() => {
    if (!showFindInStore || !currentProduct.globalSettings.findStoreUrl) {
      return
    }

    document.querySelectorAll('script[data-carabiner-find-in-store-script="true"]').forEach((node) => node.remove())

    const findInStoreContainer = document.getElementById('brauz-find-in-store-product-page')

    if (findInStoreContainer) {
      findInStoreContainer.innerHTML = ''
    }

    const script = document.createElement('script')
    script.src = currentProduct.globalSettings.findStoreUrl
    script.async = true
    script.dataset.carabinerFindInStoreScript = 'true'

    document.body.appendChild(script)

    return () => {
      script.remove()
    }
  }, [currentProduct.globalSettings.findStoreUrl, showFindInStore])

  const handleVariantClick = (variantId: string) => {
    if (String(activeVariantId) === String(variantId)) {
      return
    }

    setActiveVariantId(variantId)
  }

  const handleMaterialClick = (targetProduct: CarabinerProduct) => {
    if (targetProduct.id === currentProduct.id) {
      return
    }

    const targetVariant = getMatchingVariantForMetal(currentProduct, targetProduct, activeVariantId)

    if (!targetVariant) {
      return
    }

    setCurrentProduct(targetProduct)
    setActiveVariantId(String(targetVariant.id))
    setStatus('idle')
  }

  const handleAddToCart = async () => {
    if (!activeVariant || isSoldOut || status === 'loading') {
      return
    }

    setStatus('loading')

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: String(activeVariant.id),
              quantity: 1,
              properties: {
                _url: window.location.href
              }
            }
          ]
        })
      })

      if (!response.ok) {
        throw new Error('Failed to add Carabiner variant to cart')
      }

      setStatus('success')

      document.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: {
              name: currentProduct.title
            },
            quantity: 1
          }
        })
      )
    } catch (error) {
      console.error(error)
      setStatus('error')
    }

    window.setTimeout(() => {
      setStatus('idle')
    }, 2000)
  }

  return (
    <div className="Product__Wrapper Product__Wrapper--one-column">
      {galleryMedia.length > 0 ? (
        <div className="Product__Gallery Product__Gallery--stack">
          <div className="Product__Slideshow-wrapper">
            <div ref={gallerySliderRef} className="Product__Slideshow Product__Slideshow--desktop-scroll js-product-gallery">
              <div ref={galleryListRef} className="Product-Gallery__list Product-Gallery__list-one-column">
                {galleryMedia.map((media, index) => (
                  <div
                    key={getGalleryMediaKey(media, index)}
                    className={classNames(
                      'js-product-gallery-item StackEnabled',
                      media.type === 'video' ? 'Product__SlideItem Product__SlideItem--video' : 'Product__SlideItem Product__SlideItem--image'
                    )}
                    data-media-id={getGalleryMediaKey(media, index)}
                    data-media-position={index + 1}
                    data-media-type={media.type === 'video' ? 'video' : 'image'}
                  >
                    {media.type === 'video' && media.sources.length > 0 ? (
                      <video
                        className="block w-full aspect-[4/5] object-cover bg-[#f8f7f5]"
                        poster={getGalleryMediaPosterUrl(media) || undefined}
                        muted
                        playsInline
                        loop
                        preload={index === 0 ? 'metadata' : 'none'}
                        data-carabiner-gallery-video="true"
                      >
                        {media.sources.map((source) => (
                          <source key={source.url} src={source.url} type={source.mimeType} />
                        ))}
                      </video>
                    ) : (
                      <img
                        className="block w-full aspect-[4/5] object-cover bg-[#f8f7f5]"
                        src={getGalleryMediaPosterUrl(media) || heroImageUrl}
                        alt={activeVariant?.metafields.title || activeVariant?.title || currentProduct.title}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : 'low'}
                        decoding="async"
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="Product-Gallery__scroll"></div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="Product__InfoWrapper Right">
        <div className="Product__Info">
          <div className="Container">
            <div className="Product__Info_Top">
              <header className="flex w-full flex-col items-start gap-2">
                <div className="flex w-full items-start justify-between gap-4">
                  <h1 className="ProductMeta__Title h5 capitalize">{currentProduct.title}</h1>
                  <div className="shrink-0 leading-none">{renderWishlistButton()}</div>
                </div>
                <p className="text-subdued">{currentProductBiLineWithLength}</p>
                {activeVariant && (
                  <span className="ProductMeta__Price Price js-product-price" aria-live="polite" aria-atomic="true">
                    {formatPrice(activeVariant.price, currentProduct.tags)}
                  </span>
                )}
              </header>

              <div className="relative mt-[40px] flex flex-col">
                <div className="order-1 xl:order-2">
                  <ul className="flex gap-3 overflow-x-auto xl:grid xl:w-fit xl:grid-cols-6 2xl:grid-cols-9">
                    {currentProduct.variants.map((variant) => {
                      const isSelectedVariant = String(activeVariantId) === String(variant.id)
                      const imageUrl =
                        variant.thumbnailUrl || variant.heroImageUrl || variant.featuredImageUrl || variant.featuredImage || currentProduct.featuredImage
                      const title = variant.metafields.title || variant.title

                      return (
                        <li
                          key={variant.id}
                          className={classNames(
                            'shrink-0 h-[40px] w-[40px] border xl:h-[48px] xl:w-[48px]',
                            isSelectedVariant ? 'border-subdued' : 'border-transparent'
                          )}
                        >
                          <button
                            aria-label={title}
                            aria-pressed={isSelectedVariant}
                            className={classNames(!variant.available && 'opacity-30', 'relative block h-full w-full')}
                            onClick={() => handleVariantClick(String(variant.id))}
                            title={title}
                            type="button"
                          >
                            <img className="h-full w-full object-cover" src={imageUrl} alt={title} width="50" height="50" />
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>

                {materialOptions.length > 0 && (
                  <div className="colors-swatch-wrapper order-2 mt-5 xl:order-1 xl:mt-0">
                    <span className="colors-swatch__label">Material: </span>
                    <ul className="colors-swatch__list">
                      {materialOptions.map((item) => {
                        const isActiveMaterial = item.id === currentProduct.id
                        const formattedValue = item.value === 'YG' || item.value === 'WG' ? expandMetalLabel(item.value) : item.value

                        return (
                          <li key={item.id} className={classNames('colors-swatch__item', isActiveMaterial && 'colors-swatch__item--active')}>
                            <button type="button" onClick={() => handleMaterialClick(item.product)}>
                              <span className="uppercase">{formattedValue}</span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <div className="pb-2 text-left md:mt-5">
                <p>
                  <Text className="font-calibre text-primary" style="label">
                    {dispatchLabel}
                  </Text>
                </p>
                {dispatchDisclaimer ? <p className="mt-1 text-primary/70">{dispatchDisclaimer}</p> : null}
              </div>

              <div className="js-form-anchor-mobile">
                <div className="ProductForm__BuyButtons js-form-anchor-desktop flex flex-col">
                  <button
                    type="button"
                    className="ProductForm__AddToCart ProductForm__AddToCart_Mobile ProductForm__AddToCart_Desktop Button Button--tertiary w-full xl:w-auto"
                    disabled={status === 'loading' || isSoldOut}
                    onClick={handleAddToCart}
                  >
                    <div className="AddToCartPriceContentWrapper AddToCartPriceContentWrapper__NoPrice">
                      <span className="ProductForm_label-atc">
                        {isSoldOut ? 'Sold Out' : status === 'loading' ? '•••' : status === 'error' ? 'Error' : 'Add to Bag'}
                      </span>
                    </div>
                  </button>

                  {showNotifyMe && activeVariant ? (
                    <button
                      className="klaviyo-bis-trigger klaviyo-product-trigger Button Button--tertiary Button--full"
                      data-action="notify-me"
                      data-variant={String(activeVariant.id)}
                      type="button"
                    >
                      <div className="AddToCartPriceContentWrapper AddToCartPriceContentWrapper__NoPrice">
                        <span className="ProductForm_label-atc">{notifyMeText}</span>
                      </div>
                    </button>
                  ) : null}

                  {showFindInStore ? (
                    <>
                      <div
                        id="brauz-find-in-store-config-product-page"
                        data-sd={currentProduct.globalSettings.shopPermanentDomain}
                        data-cvi={String(currentProduct.globalSettings.firstAvailableVariantId)}
                        data-hodv={String(currentProduct.globalSettings.hasOnlyDefaultVariant)}
                        style={{ display: 'none', width: 0 }}
                      ></div>
                      <div id="brauz-find-in-store-product-page"></div>
                    </>
                  ) : null}

                  {currentProduct.globalSettings.hasSizeGuide && currentProduct.globalSettings.sizeGuideDrawerId ? (
                    <div className="additional-drawer--button pt-2">
                      <div
                        className="additional-drawer__size-guide Button Button--quaternary-dark"
                        data-action="toggle-drawer"
                        data-drawer-id={`${currentProduct.globalSettings.sizeGuideDrawerId}-drawer`}
                        aria-label="Size Guide"
                      >
                        <span>Size Guide</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {productDetailsItems.length > 0 ? (
                <div className="Product mt-8 xl:hidden">
                  <div className="accordion border-t border-[var(--nonchecked-color)]">
                    {productDetailsItems.map((item) => {
                      const isActive = activeItemId === item.id
                      const contentHeight = contentRefs.current[item.id]?.scrollHeight ?? 0

                      return (
                        <div key={item.id} className={classNames('accordion-item', { active: isActive })}>
                          <h4 className="accordion-item__heading-tag">
                            <button
                              className={classNames('accordion-item__heading', { active: isActive })}
                              id={`carabiner-accordion-control-${item.id}`}
                              type="button"
                              aria-expanded={isActive}
                              aria-controls={`carabiner-accordion-panel-${item.id}`}
                              onClick={() => {
                                setActiveItemId((currentItemId) => (currentItemId === item.id ? null : item.id))
                              }}
                            >
                              <div className="accordion-item__title">
                                <span>{item.title}</span>
                              </div>
                              <span className="accordion-item__icon" aria-hidden="true">
                                <Icon className="accordion-item__icon-open Icon--plus-accordion" type="plus-accordion" />
                                <Icon className="accordion-item__icon-close Icon--minus-accordion" type="minus-accordion" />
                              </span>
                            </button>
                          </h4>

                          <div
                            ref={(node) => {
                              contentRefs.current[item.id] = node
                            }}
                            className="accordion-item__content"
                            id={`carabiner-accordion-panel-${item.id}`}
                            role="region"
                            aria-labelledby={`carabiner-accordion-control-${item.id}`}
                            style={{ maxHeight: isActive ? `${contentHeight}px` : '0px' }}
                          >
                            <div className="accordion-item__content-inner">
                              <div className="body accordion-item__content-description Rte" dangerouslySetInnerHTML={{ __html: item.content }} />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {productDetailsItems.length > 0 ? (
                <div className="hidden xl:block">
                  <div>
                    <div className="tab-switch__titles">
                      {productDetailsItems.map((item) => {
                        const isActive = activeItemId === item.id

                        return (
                          <button
                            key={item.id}
                            type="button"
                            className={classNames('tab-switch__item js-tab-switch-title', {
                              'tab-switch__title--active': isActive
                            })}
                            data-index={item.id}
                            onClick={() => {
                              setActiveItemId(item.id)
                            }}
                          >
                            {item.title}
                          </button>
                        )
                      })}
                    </div>

                    <div className="tab-switch__contents">
                      {productDetailsItems.map((item) => {
                        const isActive = activeItemId === item.id

                        return (
                          <div
                            key={item.id}
                            className={classNames('tab-switch__item product-info Rte js-tab-switch-content', {
                              'tab-switch__content--active': isActive
                            })}
                            data-index={item.id}
                            dangerouslySetInnerHTML={{ __html: item.content }}
                          />
                        )
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
